import React, { useEffect, useState, useCallback } from "react";
import { Typography, Row, Col, InputNumber, Switch } from "antd";
import "./HeaderComponent.css";
import { usePermissions } from "./PermissionsProvider";
import ModelInput from "./ModelInput";

// Define an interface for the component props
interface ConfigComponentProps {
  models: string[];
  setModels: (models: string[]) => void;
  temperature: number;
  setTemperature: (temperature: number) => void;
  evaluations: boolean;
  setEvaluations: (evaluations: boolean) => void;
  autocorrect: boolean;
  setAutocorrect: (autocorrect: boolean) => void;
  retrieveContext: boolean;
  setRetrieveContext: (retrieveContext: boolean) => void;
  streaming: boolean;
  setStreaming: (streaming: boolean) => void;
  agentId?: number | null;
  setAgentId?: (agentId: number | null) => void;
  agentSettings?: any;
  setAgentSettings?: (settings: any | null) => void;
  onModelCategoryChange?: (category: string) => void;
}

const ConfigComponent: React.FC<ConfigComponentProps> = ({
  models,
  setModels,
  temperature,
  setTemperature,
  evaluations,
  setEvaluations: _setEvaluations,
  autocorrect: _autocorrect,
  setAutocorrect,
  retrieveContext: _retrieveContext,
  setRetrieveContext: _setRetrieveContext,
  streaming,
  setStreaming,
  agentId,
  setAgentId,
  agentSettings,
  setAgentSettings,
  onModelCategoryChange,
}) => {
  const { apiKey: _apiKey } = usePermissions();
  const [_availableModels, _setAvailableModels] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Check if an agent is selected
  const isAgentSelected = agentId !== null && agentId !== undefined;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // When evaluations is off, ensure autocorrect is off
    if (!evaluations) {
      setAutocorrect(false);
    }
  }, [evaluations, setAutocorrect]);

  const _capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Handler for model change that ensures we always have values as an array
  const _handleModelChange = useCallback(
    (value: string | string[]) => {
      if (Array.isArray(value)) {
        setModels(value);
      } else if (value) {
        setModels([value]);
      } else {
        setModels([]);
      }
    },
    [setModels]
  );

  // Memoized handler for ModelInput onChange
  const handleModelInputChange = useCallback(
    (value: string) => {
      setModels(typeof value === "string" ? [value] : []);
    },
    [setModels]
  );

  // Memoized handler for agent params change
  const handleAgentParamsChange = useCallback(
    (params: { agent_id: number | null; settings: any | null }) => {
      if (setAgentId) setAgentId(params.agent_id);
      if (setAgentSettings) setAgentSettings(params.settings);
    },
    [setAgentId, setAgentSettings]
  );

  // Responsive column spans based on screen size
  const labelSpan = isMobile ? 24 : 10;
  const controlSpan = isMobile ? 24 : 14;

  // Responsive container styles
  const containerStyle = {
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "start",
    background: "rgba(0,0,0,0.02)",
    borderRadius: "10px",
    padding: isMobile ? "10px 8px" : "20px 20px",
    gap: isMobile ? "10px" : "20px",
    boxSizing: "border-box" as const,
    maxWidth: "100%",
  };

  return (
    <div style={containerStyle}>
      <ModelInput
        value={models[0]}
        onChange={handleModelInputChange}
        placeholder="Select model"
        label=""
        showEmptyOption={false}
        modelType="all"
        agentSettings={agentSettings}
        onAgentParamsChange={handleAgentParamsChange}
        onCategoryChange={onModelCategoryChange}
        style={{ width: "100%" }}
      />
      {!isAgentSelected && (
        <Row style={{ width: "100%" }}>
          <Col span={labelSpan} style={{ marginBottom: isMobile ? "5px" : "0" }}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "rgba(0,0,0,0.8)",
                textAlign: "start",
              }}>
              Temperature
            </Typography>
          </Col>
          <Col span={controlSpan}>
            <InputNumber
              min={0}
              max={2}
              value={temperature}
              step={0.01}
              onChange={(value) => setTemperature(Number(value) || 0)}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      )}
      <Row style={{ width: "100%" }}>
        <Col span={labelSpan} style={{ marginBottom: isMobile ? "5px" : "0" }}>
          <Typography
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "rgba(0,0,0,0.8)",
              textAlign: "start",
            }}>
            Streaming
          </Typography>
        </Col>
        <Col
          span={controlSpan}
          style={{
            display: "flex",
            justifyContent: isMobile ? "start" : "end",
          }}>
          <Switch
            checked={streaming}
            onChange={(value) => setStreaming(value)}
          />
        </Col>
      </Row>
      {/* <Row style={{width: "100%"}}>
                <Col span={labelSpan} style={{marginBottom: isMobile ? "5px" : "0"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "600", color: "rgba(0,0,0,0.8)", textAlign: "start"}}>Evaluations</Typography>
                </Col>
                <Col span={controlSpan} style={{display: "flex", justifyContent: isMobile ? "start" : "end"}}>
                    <Switch
                        checked={evaluations}
                        onChange={(value) => setEvaluations(value)}
                    />
                </Col>
            </Row>
            <Row style={{width: "100%"}}>
                <Col span={labelSpan} style={{marginBottom: isMobile ? "5px" : "0"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "600", color: "rgba(0,0,0,0.8)", textAlign: "start"}}>Autocorrect</Typography>
                </Col>
                <Col span={controlSpan} style={{display: "flex", justifyContent: isMobile ? "start" : "end"}}>
                    <Switch
                        checked={autocorrect}
                        onChange={(value) => setAutocorrect(value)}
                        disabled={!evaluations}
                    />
                </Col>
            </Row> */}
    </div>
  );
};

export default ConfigComponent;
